import axios from 'axios';
import { API_URL } from '../config';

function clean(value) {
  return String(value ?? '').trim().replace(/\/+$/, '');
}

function normalizeApiRoot(value) {
  const root = clean(value);

  // Production: REACT_APP_API_URL is intentionally blank -> same-origin /api
  if (!root) return '/api';

  // Already points to /api
  if (/\/api$/i.test(root)) return root;

  // Development: http://localhost:5001 -> http://localhost:5001/api
  return `${root}/api`;
}

function normalizeUploadRoot(value) {
  const root = clean(value);

  // Production normally uses /api.
  // Development normally uses http://localhost:5001.
  return root;
}

// IMPORTANT:
// Do not use `process.env.REACT_APP_API_URL || API_URL` here because an empty
// REACT_APP_API_URL is intentional in production. Falling back to API_URL can
// accidentally point Live Classroom at a different origin.
const configuredApi =
  process.env.REACT_APP_API_URL !== undefined
    ? process.env.REACT_APP_API_URL
    : API_URL;

const API_ROOT = normalizeApiRoot(configuredApi);
const UPLOAD_ROOT = normalizeUploadRoot(
  process.env.REACT_APP_API_UPLOAD_URL
);

export function getAuthToken() {
  return localStorage.getItem('auth') || localStorage.getItem('token') || '';
}

export const liveClassroomApi = axios.create({
  baseURL: `${API_ROOT}/live-classroom`,
  timeout: 120000,
});

liveClassroomApi.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function getLiveClassroomApiRoot() {
  return `${API_ROOT}/live-classroom`;
}

export function getSocketBaseUrl() {
  // Production:
  //   API_ROOT=/api -> current origin (e.g. https://kiet.unifloo.com)
  // Development:
  //   API_ROOT=http://localhost:5001/api -> http://localhost:5001
  try {
    return new URL(API_ROOT, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
}

export function resolveBackendUrl(url) {
  if (!url) return '';

  // Preserve already-absolute URLs.
  if (/^https?:\/\//i.test(url)) return url;

  const normalizedPath = url.startsWith('/') ? url : `/${url}`;

  // Backend returns /uploads/...
  // Production: UPLOAD_ROOT=/api -> /api/uploads/...
  // Development: UPLOAD_ROOT=http://localhost:5001 -> http://localhost:5001/uploads/...
  if (UPLOAD_ROOT) {
    return `${UPLOAD_ROOT}${normalizedPath}`;
  }

  return normalizedPath;
}
